import { useState, useCallback, useEffect, useId } from 'react'
import {
  FILE_UPLOAD_LIMITS,
  buildStorageObjectPath,
  fileExtensionOf,
  isImportWarning,
  validateUploadFile,
} from '@dcp/shared'
import type { QueryKey } from '@tanstack/react-query'
import { toast } from '@atrvd/saas-ui'
import { showSessionExpiredToast } from '@/lib/session-expired-toast'
import { Upload, Loader2, AlertTriangle, Download } from 'lucide-react'
import { Button } from '@atrvd/saas-ui'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useAuth } from '@/contexts/AuthContext'
import { useQueryClient } from '@tanstack/react-query'
import { useFileImportStatus, useCreateFileImport } from '@/hooks/useBanking'
import { BANKING_KEYS } from '@/lib/query-keys'
import { getSupabaseClient } from '@/lib/supabase'
import { getSupabaseUrl, getSupabaseAnonKey } from '@/lib/runtime-env'
import { cn } from '@/lib/utils'
import { downloadBlob } from '@/lib/downloadBlob'
import { IMPORT_RUN_CEILING_MS } from '@/lib/file-import-stall'
import { isValidImportType, type FileImportRow, type ImportType } from '@/types/banking'
import { ImportHistoryTable, type ImportDetailsModal, type ImportRowActions } from './ImportHistoryTable'

/** Configuration for the FileImportSection component */
export interface FileImportConfig {
  /** Import type discriminator -- passed to create mutation and edge function */
  importType: ImportType
  /** Section heading above the upload zone */
  title: string
  /** Description text below the heading */
  description: string
  /** Required column headers for client-side validation */
  requiredHeaders: readonly string[]
  /** Optional column headers shown in template info */
  optionalHeaders: readonly string[]
  /** Worksheet name in the download template */
  templateSheetName: string
  /** File name for the download template */
  templateFileName: string
  /**
   * React Query keys to invalidate once an import reaches `completed`.
   *
   * Declared per import type rather than hard-coded in the component: the rows
   * are written server-side by a Trigger.dev task, so nothing in the browser's
   * mutation layer knows the cache went stale. Before DCP-298 this component
   * invalidated the transaction + KPI keys unconditionally, which meant a
   * successful *projections* import left the Projections table and Cash
   * Position showing pre-import data — the rows were in the database but
   * invisible until a manual reload, indistinguishable from "nothing happened".
   */
  invalidateKeys: readonly QueryKey[]
  /** Help text explaining column format (shown in the expandable "View column format" section) */
  columnFormatHelp: React.ReactNode
  /** Details modal component -- rendered when user clicks "See details" on a completed import */
  DetailsModal?: ImportDetailsModal
  /** Actions at the end of each Import History row (e.g. a kebab menu); omitted = none */
  RowActions?: ImportRowActions
}

/**
 * Upload rules for this flow — extensions, size limit, bucket and path shape —
 * all from the shared module so the client, the Edge Function and the logo
 * upload cannot drift apart.
 */
const IMPORT_RULES = FILE_UPLOAD_LIMITS.IMPORT

/** Accepted file MIME/extension string for the input element */
const ACCEPTED_TYPES = IMPORT_RULES.ACCEPT_ATTRIBUTE

/** Number of bytes to read for CSV header extraction (10 KB covers any header row) */
const CSV_HEADER_READ_SIZE = 10 * 1024

/**
 * Largest XLSX we parse in the browser to pre-check column headers.
 *
 * `read-excel-file` has no "first row only" mode, so checking headers costs a
 * full workbook parse on the main thread: the 419 KB / 15 007-row file from the
 * DCP-298 report measures ~1s and ~247 MB of allocations just to read row 0,
 * and a 50 MB one would take the tab down. Above this size we go straight to
 * the upload — the importers validate headers authoritatively server-side.
 *
 * Only the XLSX path is gated — {@link validateCsvHeaders} reads a
 * {@link CSV_HEADER_READ_SIZE} slice and is safe at any file size.
 */
const CLIENT_XLSX_VALIDATION_MAX_SIZE = 2 * 1024 * 1024

/**
 * Upper bound on the in-browser header pre-check.
 *
 * `read-excel-file` unzips sheets over 512 KB in a `blob:` Web Worker; where CSP
 * blocks it, fflate never handles the worker error and the parse promise never
 * settles, parking `handleFile` before Storage (DCP-298). On expiry we skip the
 * check and upload anyway — headers are validated authoritatively server-side.
 * 15s is loose on purpose: a false timeout costs a pointless round trip.
 */
const CLIENT_HEADER_CHECK_TIMEOUT_MS = 15_000

/** Upper bound on the `file_imports` INSERT — one small PostgREST row. */
const CREATE_RECORD_TIMEOUT_MS = 30_000

/**
 * Upper bound on the Storage upload, derived from file size.
 *
 * A fixed timeout is wrong here: the 30s that is generous for a 400 KB file
 * would false-positive on a 50 MB one over a weak link, and a false "timed
 * out" is worse than a slow upload — the user retries forever. So: a base
 * allowance for connection setup, then ~10s per megabyte (≈0.8 Mbit/s
 * sustained, below any realistic office link). The goal is not a tight bound
 * but the absence of an infinite one.
 *
 * @param fileSize - Size of the file being uploaded, in bytes
 * @returns Milliseconds to wait before giving up on the upload
 */
function uploadTimeoutMs(fileSize: number): number {
  return 45_000 + Math.ceil(fileSize / (1024 * 1024)) * 10_000
}

/**
 * Bound a promise that may never settle.
 *
 * Needed because the Storage upload cannot be cancelled: `upload()` in
 * @supabase/storage-js 2.93.3 takes `FileOptions` (cacheControl / contentType /
 * upsert / duplex / metadata / headers) and accepts no `signal` —
 * `FetchParameters.signal` exists in that package but only `download` / `list`
 * take it. So the request keeps running in the background; we stop *waiting*
 * for it and hand the user a message instead of an indefinite spinner. Without
 * this, a request that hangs without responding leaves `handleFile` parked on
 * `await` forever: no `catch`, no `finally`, no toast (DCP-298).
 *
 * A real stall detector — abort once N seconds pass with zero bytes sent —
 * needs XMLHttpRequest progress events instead of the SDK call, tracked
 * separately.
 *
 * `Promise.race` subscribes to `promise`, so a late rejection is still
 * considered handled and cannot surface as an unhandled rejection.
 *
 * @param promise - Operation to bound
 * @param timeoutMs - How long to wait before giving up
 * @param message - Error message thrown on timeout; surfaced as a toast
 * @returns The promise's value, if it settles in time
 * @throws Error with `message` when the deadline passes first
 */
async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => reject(new Error(message)), timeoutMs)
      }),
    ])
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Which step of the upload sequence is currently running.
 *
 * Named on screen rather than hidden behind one "Uploading and validating..."
 * label because the steps fail differently and the reports cannot tell them
 * apart: a browser-side parse freeze and a hung network call both read as
 * "nothing happens" (DCP-298) but have opposite fixes. With the stage visible,
 * the last thing the user saw is the diagnosis.
 */
type UploadStage = 'validating' | 'uploading' | 'starting'

/** Drop-zone copy per upload stage — see {@link UploadStage}. */
const UPLOAD_STAGE_LABELS: Record<UploadStage, { title: string; hint: string }> = {
  validating: {
    title: 'Checking file columns...',
    hint: 'Reading the header row in your browser',
  },
  uploading: {
    title: 'Uploading file...',
    hint: 'Sending the file to secure storage',
  },
  starting: {
    title: 'Starting import...',
    hint: 'Handing the file to the background importer',
  },
}

/**
 * Effective connection info, when the browser exposes it (Chromium only).
 *
 * Worth one line in the trace: it separates "the upload deadline fired because
 * the request was dead" from "because the link was genuinely slow", which is
 * the first question asked of any upload report and cannot be answered after
 * the fact.
 *
 * @returns Connection details, or `undefined` where the API is unavailable
 */
function connectionInfo(): Record<string, unknown> | undefined {
  const connection = (
    navigator as Navigator & {
      connection?: { effectiveType?: string; downlink?: number; rtt?: number }
    }
  ).connection
  if (!connection) return undefined
  return {
    effectiveType: connection.effectiveType,
    downlinkMbps: connection.downlink,
    rttMs: connection.rtt,
  }
}

/**
 * Console trace for one upload attempt.
 *
 * The sequence has four steps that fail differently and, until now, left
 * nothing behind: a browser-side parse freeze, a hung Storage request, a hung
 * `file_imports` INSERT and a rejected edge call all reached the user as the
 * same spinner (DCP-298). {@link UploadStage} names the current step on screen
 * for the user; this writes the same sequence to the console with timings,
 * which is what makes a report from a shared environment actionable: the last
 * line printed is the step that never finished, and the elapsed numbers
 * separate "slow" from "stuck".
 *
 * Traced for every upload rather than only large files — a small file that
 * misbehaves is exactly the case where the trace would otherwise be missing,
 * and one line per step costs nothing.
 *
 * The attempt id keeps concurrent or retried uploads from being read as one
 * timeline. No secrets are logged: the session token and the row payload stay
 * out, and file name / size / type are already stored on the `file_imports`
 * row.
 *
 * @param file - File being uploaded
 * @param importType - Import type this attempt belongs to
 * @returns Trace with `mark` for step transitions and `fail` for terminal errors
 */
function createImportTrace(file: File, importType: ImportType) {
  const attemptId = Math.random().toString(36).slice(2, 8)
  const startedAt = performance.now()

  /** Milliseconds since the attempt started — the "slow vs stuck" signal. */
  const elapsed = () => Math.round(performance.now() - startedAt)

  const write = (
    level: 'info' | 'error',
    step: string,
    detail?: Record<string, unknown>,
  ): void => {
    const prefix = `[file-import ${attemptId}] ${step} +${elapsed()}ms`
    if (detail) {
      console[level](prefix, detail)
    } else {
      console[level](prefix)
    }
  }

  const trace = {
    /**
     * Record a step transition.
     * @param step - Step name, e.g. `upload:start`
     * @param detail - Extra fields to log alongside it
     */
    mark(step: string, detail?: Record<string, unknown>): void {
      write('info', step, detail)
    },
    /**
     * Record a terminal failure. Logged as an error so it survives console
     * filtering set to errors only.
     * @param step - Step that failed
     * @param error - Thrown value or message
     */
    fail(step: string, error: unknown): void {
      write('error', step, {
        message: error instanceof Error ? error.message : String(error),
        error,
      })
    },
  }

  trace.mark('selected', {
    importType,
    fileName: file.name,
    sizeBytes: file.size,
    connection: connectionInfo(),
  })

  return trace
}

/**
 * How long a watched import may stay in a non-terminal state (`pending` /
 * `processing`) before the UI says so instead of spinning silently.
 *
 * A row can legitimately take a while — 15 000 projections parse + insert in
 * well under a minute, but the background job also has to be picked up first.
 * What it must never do is spin forever: if the Trigger.dev dispatch failed and
 * the failure-status write did not land, the record stays `pending` and the
 * poller (which only stops on `completed` / `failed`) keeps the spinner up with
 * no explanation. That is one of the three faces of the DCP-298 report
 * ("nothing happens").
 *
 * Kept at 5 minutes rather than raised to {@link IMPORT_RUN_CEILING_MS}: a big
 * valid import CAN still be running at this point, so the trigger is not proof
 * of a fault, and the escape hatch is worth more early than late. What the
 * review correctly objected to was the banner *claiming* a fault on that
 * evidence (DCP-298 review) — so the copy names the platform ceiling instead of
 * asserting something is wrong, which makes the message true whether the run is
 * alive or already dead.
 */
const STUCK_IMPORT_WARNING_MS = 5 * 60 * 1000

/**
 * Whole minutes, for user-facing copy derived from the constants rather than
 * written out — a hardcoded "15 minutes" in a tooltip silently starts lying the
 * moment the threshold it describes is tuned.
 */
const STUCK_IMPORT_WARNING_MINUTES = Math.round(STUCK_IMPORT_WARNING_MS / 60_000)
const IMPORT_RUN_CEILING_MINUTES = Math.round(IMPORT_RUN_CEILING_MS / 60_000)

/**
 * Longest reason quoted inside a toast before it is cut short.
 *
 * Aggregated messages carry their sample row numbers (`… — 5 rows: 8656, 8657,
 * …`), so an untruncated one can run long enough to push the rest of the toast —
 * including the pointer to the full list — off screen.
 */
const TOAST_REASON_MAX_CHARS = 140

/**
 * Pick the reason to quote out of a failed import's stored errors.
 *
 * Skips `WARNING:` entries: those are data-quality notes recorded alongside a
 * rejection (a duplicate facility name, say), not the reason the import was
 * rejected, and quoting one made a 15 000-row file look like it failed for
 * something incidental. Both importers put the aggregated validation entries
 * first, so in practice this returns `errors[0]` — the skip is what keeps that
 * true for legacy payloads and for the file-level failure paths, which do not go
 * through the aggregator.
 *
 * Every access is guarded because these rows are JSONB written by three
 * different code paths across several releases, and a missing `message` here
 * would throw inside a `useEffect` — taking the whole section down instead of
 * showing a toast (DCP-298 review).
 *
 * @param errors - `errors` payload off the file_imports row, if any
 * @returns Reason to quote, truncated for display, or `undefined` if none usable
 */
function primaryFailureReason(errors: FileImportRow['errors'] | undefined): string | undefined {
  const message = errors?.find(
    (entry) => typeof entry?.message === 'string' && !isImportWarning(entry.message),
  )?.message
  if (!message) return undefined
  return message.length > TOAST_REASON_MAX_CHARS
    ? `${message.slice(0, TOAST_REASON_MAX_CHARS - 1).trimEnd()}…`
    : message
}

/**
 * Find duplicate column header names. Comparison is case-insensitive and
 * whitespace-tolerant — matches `detectDuplicateHeaders` in
 * `@dcp/tasks/lib/file-parsers.ts`, which is the authoritative backend
 * check. Replicated here (vs. imported) because `@dcp/web` does not depend
 * on `@dcp/tasks` (Trigger.dev runtime), and the logic is small enough
 * that duplication is cheaper than a `@dcp/shared` move.
 *
 * The client check is purely a UX optimization: it stops the user from
 * spending the upload roundtrip + Trigger.dev processing time only to
 * find out the file had two `amount` columns. The backend still
 * authoritatively rejects duplicates.
 *
 * Empty / whitespace-only headers are ignored — those are caught by the
 * required-header check, not by duplicate detection.
 *
 * @param headers - Header names (already trimmed + lowercased by caller)
 * @returns Sorted, de-duplicated array of header names that appear twice
 *   or more; empty array means the file is clean.
 */
function findDuplicateHeaders(headers: string[]): string[] {
  const seen = new Set<string>()
  const duplicates = new Set<string>()
  for (const h of headers) {
    if (!h) continue
    if (seen.has(h)) {
      duplicates.add(h)
    }
    seen.add(h)
  }
  return Array.from(duplicates)
}

/**
 * Parse a single CSV line into individual fields following RFC 4180 rules.
 * Handles quoted fields that may contain commas, and escaped double-quotes
 * within quoted fields represented as `""`.
 * @param line - A single raw CSV line string
 * @returns Array of unquoted, untrimmed field values
 */
function parseCsvLine(line: string): string[] {
  const fields: string[] = []
  let current = ''
  let insideQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]

    if (insideQuotes) {
      if (char === '"') {
        // Peek ahead: two consecutive double-quotes inside a quoted field
        // represent a single literal double-quote (RFC 4180 §2.7)
        if (line[i + 1] === '"') {
          current += '"'
          i++ // skip the second quote of the escaped pair
        } else {
          // Closing quote — exit quoted mode
          insideQuotes = false
        }
      } else {
        current += char
      }
    } else {
      if (char === '"') {
        // Opening quote — enter quoted mode
        insideQuotes = true
      } else if (char === ',') {
        fields.push(current)
        current = ''
      } else {
        current += char
      }
    }
  }

  // Push the last field (no trailing comma required by RFC 4180)
  fields.push(current)

  return fields
}

/**
 * Validate CSV file headers by reading only the first line.
 * Uses FileReader.readAsText on a sliced blob to avoid loading the full file.
 * @param file - CSV File object selected by the user
 * @param requiredHeaders - Array of column names that must be present
 * @returns Validation result with found headers and optional error message
 */
async function validateCsvHeaders(
  file: File,
  requiredHeaders: readonly string[],
): Promise<{ valid: boolean; headers: string[]; error?: string }> {
  try {
    const text = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target?.result as string)
      reader.onerror = () => reject(new Error('Failed to read file'))
      // Read only the first CSV_HEADER_READ_SIZE bytes — sufficient for any header row
      reader.readAsText(file.slice(0, CSV_HEADER_READ_SIZE))
    })

    const firstLine = text.split('\n')[0]
    if (!firstLine?.trim()) {
      return { valid: false, headers: [], error: 'File is empty' }
    }
    // parseCsvLine already strips surrounding double-quotes; trim and lowercase only
    const headers = parseCsvLine(firstLine).map((h) => h.trim().toLowerCase())
    const duplicates = findDuplicateHeaders(headers)
    if (duplicates.length > 0) {
      return {
        valid: false,
        headers,
        error: `Duplicate columns detected: ${duplicates.join(', ')}`,
      }
    }
    const missing = requiredHeaders.filter((r) => !headers.includes(r))
    if (missing.length > 0) {
      return {
        valid: false,
        headers,
        error: `Missing required columns: ${missing.join(', ')}`,
      }
    }
    return { valid: true, headers }
  } catch (error) {
    console.error('Failed to validate CSV headers:', error)
    // Surface the underlying parse error to the user — "Failed to read file"
    // alone hides the actual cause (corrupted bytes, unexpected encoding,
    // permissions). Operators triaging upload reports need the message.
    const detail = error instanceof Error ? error.message : 'Unknown error'
    return { valid: false, headers: [], error: `Failed to read CSV: ${detail}` }
  }
}

/**
 * Validate XLSX file headers by reading the first row via read-excel-file.
 * Dynamically imported to keep it out of the main bundle.
 * @param file - XLSX File object selected by the user
 * @param requiredHeaders - Array of column names that must be present
 * @returns Validation result with found headers and optional error message
 */
async function validateXlsxHeaders(
  file: File,
  requiredHeaders: readonly string[],
): Promise<{ valid: boolean; headers: string[]; error?: string }> {
  try {
    // Use browser sub-path — the package has no default bare-import entry point
    const { default: readXlsxFile } = await import('read-excel-file/browser')
    const rows = await readXlsxFile(file)
    if (!rows || rows.length === 0) {
      return { valid: false, headers: [], error: 'File is empty or has no data' }
    }
    const headers = rows[0].map((cell: unknown) => String(cell ?? '').trim().toLowerCase())
    const duplicates = findDuplicateHeaders(headers)
    if (duplicates.length > 0) {
      return {
        valid: false,
        headers,
        error: `Duplicate columns detected: ${duplicates.join(', ')}`,
      }
    }
    const missing = requiredHeaders.filter((r) => !headers.includes(r))
    if (missing.length > 0) {
      return {
        valid: false,
        headers,
        error: `Missing required columns: ${missing.join(', ')}`,
      }
    }
    return { valid: true, headers }
  } catch (error) {
    console.error('Failed to validate XLSX headers:', error)
    // Include the underlying ExcelJS error so users can distinguish between
    // truly malformed files vs unsupported sheet structures vs encryption.
    const detail = error instanceof Error ? error.message : 'Unknown error'
    return { valid: false, headers: [], error: `Failed to parse Excel file: ${detail}` }
  }
}

/**
 * FileImportSection — config-driven file import flow.
 *
 * Accepts a `config` prop that determines import type, required headers,
 * template generation, description text, and which details modal to render.
 *
 * Flow:
 * 1. User drops or selects a CSV/XLSX file.
 * 2. Client-side header validation ensures required columns are present.
 * 3. File is uploaded to the `import-files` Supabase Storage bucket.
 * 4. A `file_imports` record is created with status `pending`.
 * 5. The `file-import` edge function is called to start Trigger.dev processing.
 * 6. Status is polled via useFileImportStatus until the import reaches a terminal state.
 * 7. ImportHistoryTable lists past imports with status, counts and issues.
 *
 * @param config - Configuration object defining the import type behavior
 */
/**
 * Thin guard wrapper around the hook-heavy body so an invalid
 * `config.importType` short-circuits to an in-place error block instead
 * of throwing from inside a render path (which would crash the whole
 * page without an Error Boundary above).
 *
 * Split into a wrapper + body deliberately: the body unconditionally
 * runs ~30 hooks, and the rules-of-hooks lint won't allow an early
 * return ahead of them. The wrapper performs the runtime check before
 * any hook executes, so the body never sees a bad importType.
 */
export function FileImportSection({ config }: { config: FileImportConfig }) {
  // Defense-in-depth: `FileImportConfig.importType` is typed as ImportType
  // at compile time, but a future caller (URL params, dynamic config from
  // server) could route a typo here and we'd silently dispatch to the
  // wrong Trigger.dev task or write a row with an unknown import_type
  // that fails the CHECK constraint.
  if (!isValidImportType(config.importType)) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-destructive bg-destructive/10 p-6"
      >
        <h3 className="text-lg font-medium text-destructive mb-2">
          Configuration error
        </h3>
        <p className="text-sm text-muted-foreground">
          Invalid import type: <code>{String(config.importType)}</code>. Must be{' '}
          <code>&apos;transaction&apos;</code> or <code>&apos;projection&apos;</code>.
        </p>
      </div>
    )
  }
  return <FileImportSectionBody config={config} />
}

/**
 * Hook-heavy body of FileImportSection. Invoked only after the guard
 * wrapper confirms `config.importType` is valid.
 */
function FileImportSectionBody({ config }: { config: FileImportConfig }) {
  const { currentOrganization } = useOrganization()
  const { user } = useAuth()

  const queryClient = useQueryClient()
  const createFileImport = useCreateFileImport()

  /**
   * Current step of the upload sequence, or `null` when idle. Doubles as the
   * "is an upload in flight" flag — see {@link UploadStage} for why the step
   * itself is shown to the user.
   */
  const [uploadStage, setUploadStage] = useState<UploadStage | null>(null)
  const uploading = uploadStage !== null
  const [isDragging, setIsDragging] = useState(false)

  /**
   * ID of the currently processing import.
   * Set after upload to trigger status polling.
   * Cleared once the import reaches a terminal state.
   */
  const [activeImportId, setActiveImportId] = useState<string | null>(null)

  /**
   * True once the watched import has stayed non-terminal for longer than
   * {@link STUCK_IMPORT_WARNING_MS} — flips the progress banner from
   * "everything is fine" to an actionable warning.
   */
  const [isSlowImport, setIsSlowImport] = useState(false)

  // Poll active import — refetchInterval handled inside the hook
  const { data: activeImport } = useFileImportStatus(activeImportId)

  // Arm the stuck-import timer for as long as we're watching a row. Resetting
  // on `activeImportId` change (including the clear on a terminal state) keeps
  // the warning scoped to the current import.
  useEffect(() => {
    if (!activeImportId) {
      setIsSlowImport(false)
      return
    }
    const timer = setTimeout(() => setIsSlowImport(true), STUCK_IMPORT_WARNING_MS)
    return () => clearTimeout(timer)
  }, [activeImportId])

  /**
   * Stop polling a stuck import.
   *
   * Also invalidates the import type's caches on the way out: giving up on the
   * poller means giving up the completion hook that would have refreshed them,
   * so if the job did finish while the banner was up, the data the user came
   * for is already stale. Refetching once here is cheap and strictly better
   * than leaving the page serving pre-import data (the DCP-298 symptom).
   */
  const handleStopWatching = useCallback(() => {
    setActiveImportId(null)
    for (const queryKey of config.invalidateKeys) {
      queryClient.invalidateQueries({ queryKey })
    }
    queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allFileImports })
  }, [config.invalidateKeys, queryClient])

  // When the polled import reaches a terminal state, show a result toast and clear the ID
  useEffect(() => {
    if (!activeImport || activeImportId === null) return
    if (activeImport.status !== 'completed' && activeImport.status !== 'failed') return

    if (activeImport.status === 'completed') {
      toast.success(
        `Import completed: ${activeImport.imported_rows} of ${activeImport.total_rows} rows imported`,
      )

      // Import type decides which caches went stale — see
      // `FileImportConfig.invalidateKeys` for why this is config-driven.
      for (const queryKey of config.invalidateKeys) {
        queryClient.invalidateQueries({ queryKey })
      }
    } else {
      // Counts lead, reason follows. The counts frame the scale — without them a
      // 15 000-row file that failed on 7 rows reads as if the whole file was
      // wrong — but stopping at the counts threw away the very thing the error
      // folding was built to produce: both importers now assemble the rejection
      // payload reason-first (`pushErrors(rejectionErrors, validationErrors
      // .toErrors())` before the accumulated warnings), so the first non-warning
      // entry IS the aggregated main problem, complete with occurrence count and
      // sample rows (DCP-298 review).
      //
      // Infrastructure failures are named outright instead. When a batch insert
      // fails the importers set `failed_rows = total_rows` even though every row
      // passed validation, so the counts alone would report "N of N rows invalid"
      // for what is really a database error — and a failed rollback (rows
      // possibly left behind, manual cleanup needed) would be buried in the
      // Import History issue list. These entries are found by their explicit
      // marker rather than inferred from the counters, and they are searched for
      // anywhere in the payload because the importers append them AFTER the
      // accumulated warnings.
      const invalidRows = activeImport.failed_rows ?? 0
      const scannedRows = activeImport.total_rows ?? 0
      const infrastructureError = activeImport.errors?.find(
        (entry) =>
          entry?.message?.startsWith('CRITICAL:') || entry?.message?.startsWith('Insert failed:'),
      )
      if (infrastructureError) {
        toast.error(`Import failed: ${infrastructureError.message}`)
      } else if (invalidRows > 0 && scannedRows > 0) {
        const reason = primaryFailureReason(activeImport.errors)
        toast.error(
          `Import failed: ${invalidRows} of ${scannedRows} rows invalid — no rows were imported.` +
            `${reason ? ` First problem: ${reason}` : ''}` +
            ` Expand the row in Import History to see the issues.`,
        )
      } else {
        // File-level failure (bad headers, duplicate import, storage error) —
        // there are no row counts to report, so the message itself is the point.
        const firstError = primaryFailureReason(activeImport.errors)
        toast.error(`Import failed${firstError ? `: ${firstError}` : ''}`)
      }
    }
    setActiveImportId(null)
  }, [activeImport?.status, activeImportId, activeImport, queryClient, config.invalidateKeys])

  /**
   * Core upload handler — validates, uploads to Storage, creates DB record, triggers edge function.
   * @param file - File selected or dropped by the user
   */
  const handleFile = useCallback(
    async (file: File) => {
      if (!currentOrganization?.id || !user?.id) {
        toast.error('No organization or user context')
        return
      }

      // 1-2. Extension and size, against the shared rules the server enforces
      // too. The filename is read ONLY to classify the file; it never reaches
      // the Storage path (see the upload below).
      const rejection = validateUploadFile(file, IMPORT_RULES)
      if (rejection) {
        toast.error(rejection.message)
        return
      }
      const fileType = fileExtensionOf(file.name) as 'csv' | 'xlsx'

      const trace = createImportTrace(file, config.importType)
      setUploadStage('validating')

      try {
        // 3. Validate headers client-side before uploading. Skipped for large
        //    XLSX files, where the parse itself is the hazard — see
        //    CLIENT_XLSX_VALIDATION_MAX_SIZE. The backend check is the
        //    authoritative one either way.
        const skipHeaderCheck =
          fileType === 'xlsx' && file.size > CLIENT_XLSX_VALIDATION_MAX_SIZE

        if (!skipHeaderCheck) {
          trace.mark('validate:start', { fileType })

          // Best-effort and bounded — see CLIENT_HEADER_CHECK_TIMEOUT_MS. Both
          // validators fold their own parse errors into `{ valid: false, error }`,
          // so the only way out of `withTimeout` is the deadline: the check could
          // not run at all, which is not the user's file being wrong.
          let validation: Awaited<ReturnType<typeof validateCsvHeaders>> | null = null
          try {
            validation = await withTimeout(
              fileType === 'csv'
                ? validateCsvHeaders(file, config.requiredHeaders)
                : validateXlsxHeaders(file, config.requiredHeaders),
              CLIENT_HEADER_CHECK_TIMEOUT_MS,
              `Header pre-check did not finish within ${CLIENT_HEADER_CHECK_TIMEOUT_MS / 1000}s`,
            )
          } catch (error) {
            const detail = error instanceof Error ? error.message : 'Unknown error'
            trace.mark('validate:timeout', { detail })
            console.warn('Skipping client-side header check:', detail)
          }

          if (validation && !validation.valid) {
            trace.mark('validate:reject', { reason: validation.error })
            toast.error(validation.error ?? 'Invalid file format')
            return
          }
          if (validation) trace.mark('validate:ok')
        } else {
          trace.mark('validate:skip', { reason: 'above client parse size gate' })
        }

        // 4. Upload raw file to Supabase Storage.
        // Sanitize file.name before composing the storage key — without this a
        // malicious filename like "../../other-org-id/evil.csv" would escape
        // the per-org prefix and land under another tenant's path.
        //
        // The Storage path is GENERATED, never derived from the user's
        // filename (see @dcp/shared/constants/file-upload).
        //
        // It used to be `${orgId}/${Date.now()}_${sanitisedName}`, with the
        // sanitiser stripping path separators and dot-segments — but not
        // spaces, which the `file-import` Edge Function's whitelist rejects.
        // So `dcp_projections_import_template 082526.xlsx` uploaded to Storage
        // and was then refused with "Invalid file path", leaving the
        // file_imports row created just below stuck at 'pending' forever. Both
        // shipped templates contain a space, so this hit every import.
        //
        // Generating the path removes the disagreement instead of re-tuning
        // it: the user may call the file anything at all. The original name is
        // still persisted verbatim on `file_imports.file_name`, which is what
        // the UI shows.
        const filePath = buildStorageObjectPath(
          currentOrganization.id,
          fileType,
          IMPORT_RULES.PATH_PREFIX,
        )
        const supabase = getSupabaseClient()
        setUploadStage('uploading')
        // Bounded because it cannot be cancelled — see `withTimeout`. An upload
        // that hangs without responding is the one failure mode that produced
        // no error at all: no bucket object, no toast, spinner forever.
        const uploadDeadlineMs = uploadTimeoutMs(file.size)
        trace.mark('upload:start', { filePath, deadlineMs: uploadDeadlineMs })
        const { error: uploadError } = await withTimeout(
          supabase.storage.from(IMPORT_RULES.BUCKET).upload(filePath, file),
          uploadDeadlineMs,
          'Upload timed out — the file never finished reaching storage. Check your connection and try again.',
        )

        if (uploadError) {
          // Storage answered and refused: the status/message distinguishes a
          // body-size limit from an RLS denial from a rejected mime type.
          trace.fail('upload:rejected', uploadError)
          toast.error(`Upload failed: ${uploadError.message}`)
          return
        }
        trace.mark('upload:ok')

        // 5. Create file_imports record (status: pending). Bounded for the same
        //    reason as the upload: a stalled INSERT would park the handler on
        //    `await` with the file already in the bucket and nothing on screen.
        setUploadStage('starting')
        trace.mark('insert:start')
        const importRecord = await withTimeout(
          createFileImport.mutateAsync({
            file_name: file.name,
            file_path: filePath,
            file_type: fileType,
            user_id: user.id,
            import_type: config.importType,
          }),
          CREATE_RECORD_TIMEOUT_MS,
          'Timed out while registering the import. The file was uploaded — try again in a moment.',
        )
        trace.mark('insert:ok', { fileImportId: importRecord.id })

        // 6. Call edge function to kick off Trigger.dev background job
        const {
          data: { session },
        } = await supabase.auth.getSession()

        if (!session?.access_token) {
          trace.mark('trigger:no-session')
          showSessionExpiredToast()
          return
        }

        // Runtime Supabase config (window.__ENV__ / config.js, falling back to
        // VITE_*). getSupabase* throw when it is missing: catch that, show a
        // readable toast and log the error — without the log the cause of the
        // failure would have to be guessed.
        let supabaseUrl: string
        let supabaseAnonKey: string
        try {
          supabaseUrl = getSupabaseUrl()
          supabaseAnonKey = getSupabaseAnonKey()
        } catch (error) {
          trace.fail('trigger:no-config', error)
          console.error('Supabase runtime config unavailable:', error)
          toast.error('Missing Supabase configuration')
          return
        }

        // Bound the edge-function call with an AbortController. The function
        // only kicks off a background Trigger.dev task — it should return in
        // well under a second. 30s is generous enough to absorb cold-starts
        // / network blips and short enough that the user sees a focused
        // toast instead of an indefinite spinner if the function hangs.
        const FILE_IMPORT_TRIGGER_TIMEOUT_MS = 30_000
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), FILE_IMPORT_TRIGGER_TIMEOUT_MS)

        let response: Response
        try {
          trace.mark('trigger:start', { deadlineMs: FILE_IMPORT_TRIGGER_TIMEOUT_MS })
          response = await fetch(`${supabaseUrl}/functions/v1/file-import`, {
            method: 'POST',
            signal: controller.signal,
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              apikey: supabaseAnonKey,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              organizationId: currentOrganization.id,
              fileImportId: importRecord.id,
              filePath,
              fileType,
              importType: config.importType,
            }),
          })
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') {
            trace.fail('trigger:timeout', err)
            toast.error('Import request timed out — try again in a moment.')
            return
          }
          trace.fail('trigger:network-error', err)
          throw err
        } finally {
          clearTimeout(timeoutId)
        }

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: 'Unknown error' })) as { error?: string }
          trace.fail('trigger:rejected', `HTTP ${response.status}: ${errorData.error ?? 'Unknown error'}`)
          toast.error(`Failed to start import: ${errorData.error ?? 'Unknown error'}`)
          return
        }

        // 7. Begin polling for status updates
        trace.mark('watch:start', { fileImportId: importRecord.id })
        setActiveImportId(importRecord.id)
        toast.success('File uploaded. Processing started...')
      } catch (error) {
        // Deadline expiries land here too — the message names the step, and the
        // preceding `*:start` line is the one with no `:ok` after it.
        trace.fail('aborted', error)
        const message = error instanceof Error ? error.message : 'Upload failed'
        toast.error(message)
      } finally {
        setUploadStage(null)
      }
    },
    [currentOrganization?.id, user?.id, createFileImport, config.requiredHeaders, config.importType],
  )

  /**
   * Handle file drop on the drop zone.
   * Only processes the first dropped file.
   */
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      const file = e.dataTransfer.files[0]
      if (file) void handleFile(file)
    },
    [handleFile],
  )

  /**
   * Handle native file input change event.
   * Resets the input value after selection to allow re-uploading the same file.
   */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) void handleFile(file)
      e.target.value = ''
    },
    [handleFile],
  )

  /**
   * Unique ID for the hidden file input. We compose `useId()` with the
   * `importType` so the id is both guaranteed-unique-per-instance (React
   * gives every component a different `useId()` value, even when two
   * instances are rendered on the same page with the same importType)
   * AND scoped/labelled so it remains debuggable in the DOM inspector.
   * The previous form `file-import-input-${importType}` collided whenever
   * two sections of the same type were mounted simultaneously, which
   * would route a click on one drop-zone to the other section's file
   * picker.
   */
  const reactId = useId()
  const fileInputId = `file-import-input-${config.importType}-${reactId}`

  /** Open the hidden file input when the drop zone is clicked */
  const handleDropZoneClick = useCallback(() => {
    document.getElementById(fileInputId)?.click()
  }, [fileInputId])

  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false)

  /** Download an XLSX template with required and optional headers highlighted */
  const handleDownloadTemplate = useCallback(async () => {
    setIsGeneratingTemplate(true)
    try {
      const { Workbook } = await import('exceljs')
      const workbook = new Workbook()
      const sheet = workbook.addWorksheet(config.templateSheetName)

      const allHeaders = [...config.requiredHeaders, ...config.optionalHeaders]
      const headerRow = sheet.addRow(allHeaders)

      // Padding added to header text length for comfortable column width
      const COLUMN_WIDTH_PADDING = 4
      // Minimum column width in characters
      const MIN_COLUMN_WIDTH = 14

      // Style each header cell — required columns get a colored background
      headerRow.eachCell((cell, colNumber) => {
        const isRequired = colNumber <= config.requiredHeaders.length
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: isRequired ? 'FFDC2626' : 'FF6B7280' },
        }
        cell.border = {
          bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        }
      })

      // Auto-fit column widths based on header length
      sheet.columns = allHeaders.map((h) => ({ width: Math.max(h.length + COLUMN_WIDTH_PADDING, MIN_COLUMN_WIDTH) }))

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      downloadBlob(blob, config.templateFileName)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to generate template'
      toast.error(`Template download failed: ${message}`)
    } finally {
      setIsGeneratingTemplate(false)
    }
  }, [config.templateSheetName, config.templateFileName, config.requiredHeaders, config.optionalHeaders])

  const isProcessing =
    activeImport?.status === 'pending' || activeImport?.status === 'processing'

  return (
    <div className="space-y-6">
      {/* Upload Zone */}
      <div className="rounded-lg border bg-card p-6">
        <h3 className="text-lg font-medium mb-1">{config.title}</h3>
        <div className="flex items-start justify-between mb-4">
          <p className="text-sm text-muted-foreground">
            {config.description}
          </p>
          <Button variant="outline" size="sm" className="ml-4 shrink-0" onClick={handleDownloadTemplate} disabled={isGeneratingTemplate}>
            {isGeneratingTemplate ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-1.5" />
            )}
            {isGeneratingTemplate ? 'Generating...' : 'Download Template'}
          </Button>
        </div>

        {/* Drop zone — click or drag-and-drop */}
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload file — click or drag and drop"
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault()
            setIsDragging(true)
          }}
          onDragLeave={() => setIsDragging(false)}
          onClick={handleDropZoneClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleDropZoneClick()
          }}
          className={cn(
            'cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors outline-none',
            isDragging
              ? 'border-primary bg-primary/10'
              : 'border-border bg-muted/50 hover:border-muted-foreground hover:bg-muted/80',
            uploading && 'pointer-events-none opacity-50',
          )}
        >
          <input
            id={fileInputId}
            type="file"
            accept={ACCEPTED_TYPES}
            onChange={handleInputChange}
            className="hidden"
            disabled={uploading}
            aria-hidden="true"
          />
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            {uploading ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <Upload className="h-6 w-6" />
            )}
          </div>
          {/* Name the current step rather than a generic "please wait": the
              steps fail in different ways and reports could not tell them
              apart (DCP-298). See UploadStage. */}
          <p className="mb-1 text-sm font-medium">
            {uploadStage ? UPLOAD_STAGE_LABELS[uploadStage].title : 'Drop your file here'}
          </p>
          <p className="text-xs text-muted-foreground">
            {uploadStage
              ? UPLOAD_STAGE_LABELS[uploadStage].hint
              : 'or click to browse — CSV, XLSX up to 50 MB'}
          </p>
        </div>

        {/* Active import progress banner. Turns into a warning once the import
            has been non-terminal for STUCK_IMPORT_WARNING_MS — an endless
            spinner is indistinguishable from "nothing happened" (DCP-298). */}
        {activeImport && isProcessing && (
          <div
            className={cn(
              'mt-4 flex items-start justify-between gap-3 rounded-lg border p-4',
              isSlowImport ? 'border-amber-200 bg-amber-50' : 'border-blue-200 bg-blue-50',
            )}
          >
            <div className="flex items-start gap-3 min-w-0">
              {isSlowImport ? (
                <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
              ) : (
                <Loader2 className="h-5 w-5 animate-spin text-blue-500 shrink-0" />
              )}
              <div className="min-w-0">
                <p
                  className={cn(
                    'text-sm font-medium',
                    isSlowImport ? 'text-amber-800' : 'text-blue-800',
                  )}
                >
                  Processing: {activeImport.file_name}
                </p>
                <p className={cn('text-xs', isSlowImport ? 'text-amber-700' : 'text-blue-600')}>
                  {isSlowImport
                    ? `Still running after ${STUCK_IMPORT_WARNING_MINUTES} minutes. Imports are capped at ${IMPORT_RUN_CEILING_MINUTES} minutes, so this will either finish or stop on its own — use Refresh below to check Import History, or stop watching and come back later.`
                    : activeImport.status === 'pending'
                      ? 'Waiting to start...'
                      : 'Processing rows...'}
                </p>
              </div>
            </div>
            {isSlowImport && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={handleStopWatching}
              >
                Stop watching
              </Button>
            )}
          </div>
        )}

        {/* Template info toggle */}
        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground select-none">
            View column format
          </summary>
          <p className="mt-2 text-xs text-muted-foreground">
            File must contain the following header row:
          </p>
          <div className="mt-1 rounded bg-muted/50 p-3 text-xs font-mono text-muted-foreground">
            {[...config.requiredHeaders].join(', ')}{config.optionalHeaders.length > 0 ? `, ${[...config.optionalHeaders].join(', ')}` : ''}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {config.columnFormatHelp}
          </p>
        </details>
      </div>

      <ImportHistoryTable
        importType={config.importType}
        DetailsModal={config.DetailsModal}
        RowActions={config.RowActions}
      />
    </div>
  )
}
